<script setup lang="ts">
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'

const { locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const getLocalizedText = (
  value: FeedActivity['title'] | FeedActivity['description'] | string | null | undefined
): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[locale.value as keyof typeof value] || value.en || ''
  }
  return ''
}

const isUsedType = computed(() => {
  return props.activity.type === 'reward.used'
})

const rewardName = computed(() => {
  return props.activity.data?.reward_name || props.activity.data?.text || ''
})

const descriptionText = computed(() => {
  return getLocalizedText(props.activity.description)
})

const translationKey = computed(() => {
  if (isUsedType.value) {
    return 'pages.customer.customerDetailPage.blocks.activity.reward.used'
  }
  return 'pages.customer.customerDetailPage.blocks.activity.reward.text'
})

const title = computed(() => {
  return getLocalizedText(props.activity.title) || ''
})

const iconColor = computed(() => {
  return 'bg-primary'
})
</script>

<template>
  <ActivityCardBase
    :icon="activity.icon || 'HeroGiftIconSolid'"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template v-if="title" #title>
      <p class="text-sm font-medium text-foreground">{{ title }}</p>
    </template>
    <template #description>
      <p
        v-if="descriptionText"
        :class="[
          'text-sm text-muted-foreground',
          {
            'mt-1': title
          }
        ]"
      >
        {{ descriptionText }}
      </p>
      <i18n-t
        v-else
        :keypath="translationKey"
        tag="p"
        :class="[
          'text-sm text-muted-foreground',
          {
            'mt-1': title
          }
        ]"
      >
        <template #reward>
          <span class="font-medium">{{ rewardName }}</span>
        </template>
      </i18n-t>
    </template>
  </ActivityCardBase>
</template>
